/* Feb-2026 · Task Launcher — "what do you want to do next?" home screen.
 * Replaces the center column's default view for brand-new sessions
 * (see AppShell's DEFAULT_VISIBLE) and is reachable any time via the
 * Home icon in the ActivityBar. Plain-language cards route to the
 * exact same actions/dialogs the toolbar already offers — nothing new
 * was built underneath, this is just a friendlier front door.
 */
import React from 'react';
import { usePW } from '@/lib/store';
import { PenLine, MessageCircleHeart, ScrollText, Rocket } from 'lucide-react';
import { setPendingSynopsisOpen, setPendingChatPrompt } from '@/lib/pendingIntents';

const FEEDBACK_PROMPT = "Please review my latest chapter and tell me how it's going — pacing, dialogue, anything that feels weak or could be stronger.";

export default function HomeLauncherPanel({ togglePanel }) {
  const { project, chapters } = usePW();
  const draftedCount = (chapters || []).filter((c) => (c.content || '').trim()).length;

  const go = (panel) => {
    togglePanel('home', false);
    togglePanel(panel, true);
  };

  const CARDS = [
    {
      id: 'write',
      icon: PenLine,
      title: 'Write my next chapter',
      body: 'Jump back into the manuscript and keep going.',
      onClick: () => go('editor'),
    },
    {
      id: 'feedback',
      icon: MessageCircleHeart,
      title: "Get feedback on what I've written",
      body: 'Ask Claude how your latest chapter is reading.',
      onClick: () => {
        // Keep the editor open too, so the center column isn't left
        // blank, and set the pending prompt BEFORE the chat panel
        // mounts (a fresh mount won't catch an event fired earlier).
        setPendingChatPrompt(FEEDBACK_PROMPT);
        togglePanel('home', false);
        togglePanel('editor', true);
        togglePanel('chat', true);
        window.dispatchEvent(new CustomEvent('loom:chat-seed-prompt', { detail: { prompt: FEEDBACK_PROMPT } }));
      },
    },
    {
      id: 'synopsis',
      icon: ScrollText,
      title: 'Generate a synopsis',
      body: 'Summarize the story so far, or start one from an idea.',
      onClick: () => {
        // The Generate Synopsis dialog lives inside the editor panel —
        // switch to it first, and set a pending flag for the case
        // where the editor wasn't already mounted.
        setPendingSynopsisOpen();
        go('editor');
        window.dispatchEvent(new CustomEvent('loom:open-manuscript-synopsis'));
      },
    },
    {
      id: 'publish',
      icon: Rocket,
      title: 'Publish my story',
      body: 'Send your manuscript out into the world.',
      onClick: () => window.dispatchEvent(new CustomEvent('loom:open-publish')),
    },
  ];

  return (
    <div className="flex h-full w-full items-center justify-center overflow-auto p-8" data-testid="home-launcher-panel">
      <div className="w-full max-w-xl">
        <h1 className="mb-1 text-2xl font-semibold text-foreground">
          {project?.name ? `Welcome back to \u201c${project.name}\u201d` : 'What do you want to do next?'}
        </h1>
        <p className="mb-6 text-[13px] text-muted-foreground">
          {draftedCount > 0
            ? `${draftedCount} chapter${draftedCount === 1 ? '' : 's'} drafted so far. Pick a next step.`
            : 'Pick a next step — you can always come back here from the Home icon.'}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.id}
                type="button"
                onClick={card.onClick}
                data-testid={`home-launcher-${card.id}`}
                className="flex flex-col items-start gap-2 rounded-xl border border-border bg-card p-4 text-left transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
              >
                <Icon className="h-5 w-5 text-primary" />
                <span className="text-[14px] font-semibold text-foreground">{card.title}</span>
                <span className="text-[13.5px] text-muted-foreground">{card.body}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
